import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useChecklistTemplates, type ChecklistTemplate } from '@/hooks/useChecklists';
import { useInspectionTemplates, type InspectionTemplate } from '@/hooks/useInspections';

export type CreateChecklistKind = 'checklist' | 'inspection';

export interface CreateChecklistSelection {
  kind: CreateChecklistKind;
  templateId: string;
  name: string;
}

interface ChecklistTemplateSelectProps {
  value: CreateChecklistSelection[];
  onChange: (next: CreateChecklistSelection[]) => void;
  /** Selected work-order service IDs — used to hide templates auto-create would attach. */
  serviceIds?: string[];
}

/**
 * True when `POST /checklists/auto/:bookingId` would attach this template
 * (default, or linked to one of the work order's services). Matches the
 * picker count in ServiceBookChecklistsCard, not the backend's extra
 * empty-`serviceIds` match. Those templates are omitted here so a later
 * auto-create cannot duplicate a list the user also picked (#2128).
 */
function isAutoLinkedChecklistTemplate(
  template: Pick<ChecklistTemplate, 'isDefault' | 'serviceIds'>,
  serviceIds: string[],
): boolean {
  if (template.isDefault) return true;
  if (template.serviceIds && template.serviceIds.length > 0) {
    return template.serviceIds.some((sid) => serviceIds.includes(sid));
  }
  return false;
}

function isSelected(
  value: CreateChecklistSelection[],
  kind: CreateChecklistKind,
  templateId: string,
): boolean {
  return value.some((item) => item.kind === kind && item.templateId === templateId);
}

function toggleSelection(
  value: CreateChecklistSelection[],
  item: CreateChecklistSelection,
): CreateChecklistSelection[] {
  if (isSelected(value, item.kind, item.templateId)) {
    return value.filter((s) => !(s.kind === item.kind && s.templateId === item.templateId));
  }
  return [...value, item];
}

export function ChecklistTemplateSelect({
  value,
  onChange,
  serviceIds = [],
}: ChecklistTemplateSelectProps) {
  const { t } = useTranslation('workorders');
  const { data: checklistTemplatesData, isLoading: checklistsLoading } = useChecklistTemplates();
  const {
    data: inspectionTemplatesData,
    isLoading: inspectionsLoading,
    isError: inspectionTemplatesError,
  } = useInspectionTemplates();

  const inspectionTemplates: InspectionTemplate[] = inspectionTemplatesError
    ? []
    : (inspectionTemplatesData?.data ?? []);

  const selectableChecklists = useMemo(() => {
    const templates: ChecklistTemplate[] = checklistTemplatesData ?? [];
    return templates.filter((tmpl) => !isAutoLinkedChecklistTemplate(tmpl, serviceIds));
  }, [checklistTemplatesData, serviceIds]);

  const autoLinkedCount = useMemo(() => {
    const templates: ChecklistTemplate[] = checklistTemplatesData ?? [];
    return templates.filter((tmpl) => isAutoLinkedChecklistTemplate(tmpl, serviceIds)).length;
  }, [checklistTemplatesData, serviceIds]);

  const isLoading = checklistsLoading || inspectionsLoading;
  const isEmpty = selectableChecklists.length === 0 && inspectionTemplates.length === 0;

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', py: 0.5 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ width: '100%' }} role="group" aria-label={t('create.checklistsSection')}>
      <Typography variant="body2" color="text.secondary">
        {t('create.checklistsOptionalHint')}
      </Typography>
      {autoLinkedCount > 0 && (
        <Typography variant="caption" color="text.secondary">
          {t('create.checklistsAutoLinkedHint')}
        </Typography>
      )}
      {isEmpty ? (
        <Typography variant="body2" color="text.secondary">
          {t('create.checklistsEmpty')}
        </Typography>
      ) : (
        <>
          {inspectionTemplates.length > 0 && (
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {t('create.inspectionTemplates')}
              </Typography>
              <FormGroup>
                {inspectionTemplates.map((tmpl) => (
                  <FormControlLabel
                    key={tmpl.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={isSelected(value, 'inspection', tmpl.id)}
                        onChange={() =>
                          onChange(
                            toggleSelection(value, {
                              kind: 'inspection',
                              templateId: tmpl.id,
                              name: tmpl.name,
                            }),
                          )
                        }
                      />
                    }
                    label={tmpl.name}
                  />
                ))}
              </FormGroup>
            </Box>
          )}
          {selectableChecklists.length > 0 && (
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {t('create.checklistTemplates')}
              </Typography>
              <FormGroup>
                {selectableChecklists.map((tmpl) => (
                  <FormControlLabel
                    key={tmpl.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={isSelected(value, 'checklist', tmpl.id)}
                        onChange={() =>
                          onChange(
                            toggleSelection(value, {
                              kind: 'checklist',
                              templateId: tmpl.id,
                              name: tmpl.name,
                            }),
                          )
                        }
                      />
                    }
                    label={tmpl.name}
                  />
                ))}
              </FormGroup>
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}
